import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IAgentRetrievalTestingPayload,
  IGetAgentsPayload,
  IProcessFilesPayload,
} from '../types/agent.service.type';
import { agentService } from '../services/agent.service';
import { processFileStream } from '../utils/process-file-stream';

export const useGetAgents = (options: IGetAgentsPayload) => {
  return useQuery({
    queryKey: ['agents', options],
    queryFn: () => agentService.getAgents(options),
  });
};

export const useProcessFiles = () => {
  return useMutation({
    mutationKey: ['agent', 'process-files'],
    mutationFn: async (payload: IProcessFilesPayload) => {
      const stream = await agentService.processFiles(payload);
      const reader = stream.getReader();
      return processFileStream(reader);
    },
  });
};

export const useUpdateAgentAIConfigurations = () => {
  return useMutation({
    mutationKey: ['agent', 'update-ai-configuration'],
    mutationFn: agentService.updateAgentAIConfiguration,
  });
};

export const useUpdateAgentPersona = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['agents', 'update-persona'],
    mutationFn: agentService.updateAgentPersona,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });
};

export const useUpdateWidget = () => {
  return useMutation({
    mutationKey: ['agent', 'widget'],
    mutationFn: agentService.updateAgentWidget,
  });
};

export const useQuerySSE = () => {
  return {
    streamQuery: agentService.querySSE,
  };
};

export const useGetAgentById = (
  id: string,
  options?: {
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['agent', id],
    queryFn: () =>
      agentService.getAgentById({ id, projectKey: import.meta.env.VITE_X_BLOCKS_KEY || '' }),
    enabled: options?.enabled ?? true,
  });
};

export const useAddAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['agent', 'add'],
    mutationFn: agentService.addAgent,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
  });
};

export const useGetLlmModels = () => {
  return useQuery({
    queryKey: ['llm-models'],
    queryFn: () => agentService.getllmModels(),
  });
};

export const useQueryAgent = () => {
  return useMutation({
    mutationKey: ['agent', 'query'],
    mutationFn: agentService.query,
  });
};

export const usePublishAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['agent', 'publish'],
    mutationFn: agentService.publishAgents,
    onSuccess: (res) => {
      if (res.is_success)
        queryClient.invalidateQueries({
          queryKey: ['agents'],
        });
    },
  });
};

export const useDeleteAgentById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['agent', 'delete'],
    mutationFn: agentService.deleteAgentById,
    onSuccess: (res) => {
      if (res.is_success) queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useWidgetColorSuggestion = () => {
  return useMutation({
    mutationKey: ['widget', 'color-suggestion'],
    mutationFn: agentService.widgetColorSuggestion,
  });
};
export const useAgentRetrievalTesting = (agent_id: string) => {
  return useMutation({
    mutationKey: ['agent', 'retrieval-testing', agent_id],
    mutationFn: (body: IAgentRetrievalTestingPayload) =>
      agentService.retrievalTesting(agent_id, body),
  });
};
